import React, { useEffect, useState } from "react";

import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { addAllWidgets } from "api/report";

import { DURATION_RANGE, DURATION_SESSION_KEY, isCurrentLayoutEmpty } from "utils/reportUtils";
import { getItemSession, saveItemSession } from "utils/session";
import { formatDate, getEndDate, getStartDate, saveDisplayDates } from "utils/utils";
import { ReportGenerateStrings } from "constants/constantStrings";
import { LOCAL_TZ_CODE } from "constants/globalConstants";
import { SYSTEMS_PAGES } from "constants/pages.constant";

import NoItemsLayout from "pages/Dashboard/NoItemsLayout";
import BadgeLabel from "components/BadgeLabel/index.js";
import DatePopover from "components/DatePopover/DatePopover";
import DupontButton from "components/DupontButton";
import DupontCheckbox from "components/DupontCheckbox";

import ReportPreview from "../ReportPreview";

import "./index.scss";

const ReportGenerate = ({
  systemID,
  page,
  currentLayout,
  isReportGenerateEnable,
  setUpdateReportPreview,
  reports,
  setWidgets,
  isChecked,
  isIndeterminate
}) => {
  const currentDate = new Date();
  const currentDateString = currentDate.toString();
  const pastDate = new Date();
  const getLastWeekDate = currentDate.getDate() - 7;
  pastDate.setDate(getLastWeekDate);
  const lastWeekString = pastDate.toString();

  const [optionChecked, setOptionChecked] = useState(getItemSession(DURATION_SESSION_KEY.DATE_RANGE) || DURATION_RANGE.WEEK);

  const [downloadDateRange, setDownloadDateRange] = useState({
    dateRange: getItemSession(DURATION_SESSION_KEY.DATE_RANGE) || DURATION_RANGE.WEEK,
    startDateDisplay: getItemSession(DURATION_SESSION_KEY.START_DATE_DISPLAY) || formatDate(lastWeekString),
    endDateDisplay: getItemSession(DURATION_SESSION_KEY.END_DATE_DISPLAY) || formatDate(currentDateString),
    startDateCustom: null,
    endDateCustom: null
  });

  const [showPreview, setShowPreview] = useState(false);

  const [selectedStartEndDates, setSelectedStartEndDates] = useState({
    startDate: getStartDate(),
    endDate: getEndDate()
  });

  const [isError, setDateError] = useState(false);
  const [report, setReport] = useState(reports);
  const [isEmptyLayout, setIsEmptyLayout] = useState(reports?.length === 0 || isCurrentLayoutEmpty(currentLayout));

  const objAppInsights = useAppInsightsHandler();

  useEffect(() => {
    if (downloadDateRange.dateRange === DURATION_RANGE.CUSTOM) {
      const { startDate, endDate } = selectedStartEndDates;
      if (startDate !== null && endDate !== null) {
        const startDateDisplay = formatDate(startDate.toString());
        const endDateDisplay = formatDate(endDate.toString());

        setDownloadDateRange({
          dateRange: DURATION_RANGE.CUSTOM,
          startDateDisplay,
          startDateCustom: startDate.unix(),
          endDateDisplay,
          endDateCustom: endDate.unix()
        });

        saveDisplayDates(startDateDisplay, endDateDisplay);

        saveItemSession(DURATION_SESSION_KEY.START_DATE, startDate);
        saveItemSession(DURATION_SESSION_KEY.END_DATE, endDate);
        setUpdateReportPreview(true);
      }

      setDateError(false);

      if (downloadDateRange.dateRange === DURATION_RANGE.CUSTOM && (startDate === null || endDate === null || startDate > endDate)) {
        setDateError(true);
      }
    }
  }, [selectedStartEndDates]);

  useEffect(() => {
    setIsEmptyLayout(reports?.length === 0 || isCurrentLayoutEmpty(currentLayout));
  }, [reports, currentLayout]);

  const handleCustomDateChange = () => {
    const startDate = getStartDate();
    const endDate = getEndDate();

    if (startDate && endDate) {
      const startDateDisplay = formatDate(startDate.toString());
      const endDateDisplay = formatDate(endDate.toString());

      setDownloadDateRange({
        dateRange: DURATION_RANGE.CUSTOM,
        startDateDisplay,
        endDateDisplay,
        startDateCustom: startDate.unix(),
        endDateCustom: endDate.unix()
      });
      saveItemSession(DURATION_SESSION_KEY.DATE_RANGE, DURATION_RANGE.CUSTOM);
      saveDisplayDates(startDateDisplay, endDateDisplay);
      setUpdateReportPreview(true);
    }
  };

  const handleDateChange = optionChecked => {
    objAppInsights.trackEvent("dateChange");
    if (optionChecked === DURATION_RANGE.CUSTOM) {
      handleCustomDateChange();
    } else {
      setDateError(false);
      let getLastDate;
      const currentDateObj = currentDate.getDate();
      switch (optionChecked) {
        case DURATION_RANGE.DAY:
          getLastDate = currentDateObj - 1;
          break;
        case DURATION_RANGE.WEEK:
          getLastDate = currentDateObj - 7;
          break;
        case DURATION_RANGE.MONTH:
          getLastDate = currentDate.getMonth() - 1;
          break;
        default:
          getLastDate = currentDateObj - 7;
      }

      const pastDate = new Date();
      if (optionChecked === DURATION_RANGE.MONTH) {
        pastDate.setMonth(getLastDate);
      } else {
        pastDate.setDate(getLastDate);
      }

      const lastDateString = pastDate.toString();

      const startDateDisplay = formatDate(lastDateString);
      const endDateDisplay = formatDate(currentDateString);

      setDownloadDateRange({
        dateRange: optionChecked,
        startDateDisplay,
        endDateDisplay,
        startDateCustom: null,
        endDateCustom: null
      });
      saveItemSession(DURATION_SESSION_KEY.DATE_RANGE, optionChecked);
      saveDisplayDates(startDateDisplay, endDateDisplay);
      setUpdateReportPreview(true);
    }
  };

  const handleGenerateReport = () => {
    objAppInsights.trackEvent("generateReport");
    setReport(reports.filter(item => item.Enabled));
    setShowPreview(true);
  };

  const onValueChange = event => {
    objAppInsights.trackEvent("dateRangeChange");
    setOptionChecked(event.target.value);
    handleDateChange(event.target.value);
  };

  const handleClose = () => {
    setShowPreview(false);
  };

  const badgeError = isError && downloadDateRange.dateRange === DURATION_RANGE.CUSTOM;

  const getCurrentLayoutName = () => {
    const layout = currentLayout[systemID];
    return (layout && layout[page]?.Name) || "";
  };

  const onAllWidgetsChanged = checked => {
    const updatedWidgets = reports.map(obj => ({ ...obj, Enabled: checked }));
    setWidgets(updatedWidgets);
    addAllWidgets({
      layoutID: currentLayout[systemID][page].ID,
      Enabled: checked
    });
  };

  return (
    <div className="displayBlock">
      <div className="margin-right-20">
        <div className="flex-between">
          <BadgeLabel
            customClass="font_weight_100"
            labelColor={!isEmptyLayout ? "report-badge-label" : "report-badge-label--disabled"}
            badgeDescription={ReportGenerateStrings.selectRange}
            error={badgeError}
            badgeDisable={isEmptyLayout}
            badgeText={
              badgeError
                ? ReportGenerateStrings.dateError
                : `${downloadDateRange.startDateDisplay} to ${downloadDateRange.endDateDisplay} ${LOCAL_TZ_CODE}`
            }
          />
          <div>
            <DupontButton type="primary" onClick={handleGenerateReport} disabled={isError || !isReportGenerateEnable}>
              {ReportGenerateStrings.generateReport}
            </DupontButton>
          </div>
        </div>
        <div className="d-flex marginTop10">
          <div className="d-flex marginTop10">
            {Object.keys(DURATION_RANGE).map(rangeKey => {
              const rangeValue = DURATION_RANGE[rangeKey];
              return (
                <label key={rangeValue} disabled={isEmptyLayout} className="radiocontainer">
                  {ReportGenerateStrings[rangeValue]}
                  <input
                    className="radioLabel"
                    type="radio"
                    disabled={isEmptyLayout}
                    value={rangeValue}
                    onChange={onValueChange}
                    checked={optionChecked === rangeValue}
                  />
                  <span className="radioButton" />
                </label>
              );
            })}
            {
              <div className="date-icon">
                {optionChecked === DURATION_RANGE.CUSTOM && !isEmptyLayout && (
                  <DatePopover
                    setSelectedStartEndDates={setSelectedStartEndDates}
                    page={SYSTEMS_PAGES.REPORTS}
                    timeEnabled={true}
                    startDate={selectedStartEndDates.startDate}
                    endDate={selectedStartEndDates.endDate}
                  />
                )}
              </div>
            }
          </div>
        </div>
        {!isEmptyLayout && (
          <div className="marginBtm10 flex-between-center">
            <div className="flex-center-center">
              <DupontCheckbox onChange={onAllWidgetsChanged} checked={isChecked} indeterminate={isIndeterminate} />
              <div className="report-all-widgets">{ReportGenerateStrings.addAllWidgets}</div>
            </div>
            <div className="bodySecondaryHeadingText jc-end">{ReportGenerateStrings.moveWidgets}</div>
          </div>
        )}
      </div>
      {showPreview && (
        <ReportPreview
          showPreview={showPreview}
          handleClose={handleClose}
          reports={report}
          layoutID={currentLayout[systemID][page].ID}
          layoutName={currentLayout[systemID][page].Name}
        />
      )}
      {isEmptyLayout && <NoItemsLayout currentLayout={getCurrentLayoutName()} page={page} />}
    </div>
  );
};

export default ReportGenerate;
